import { FilterAction } from '@/types/SearchFacet';
import { firstTimeWalkthroughSkip } from '@/components/OnboardingWalkthrough/components/skips';
import { FirstTimeWalkthroughSteps } from '@/types/walkthroughs';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { getCategoryLandingPageUrl, getPriceGuideUrl, getSearchUrlWithParams } from '@/utils/urls';
import {
    onboardingWalkthroughCurrentStep,
    onboardingWalkthroughIsOpen,
} from '@/redux/modules/walkthroughs/walkthroughs.selectors';
import { SearchSortAndDirection } from '@/types/search/enums/sortTypes';
import { setOnboardingWalkthroughOpenProperty } from '@/redux/modules/walkthroughs/walkthroughs.reducer';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useNavigate } from 'react-router-dom';
import React, { ReactElement, ReactNode } from 'react';
import styled from 'styled-components';
import Tab from '@liveauctioneers/caterwaul-components/lib/Tab/Tab';

export type StatusTabValue = 'online' | 'archive';

type StatusTabData = {
    label: ReactNode;
    value: StatusTabValue;
};

export type SearchStatusTabsProps = {
    /**
     * Which tab is currently active between 'online' and 'archive'
     */
    activeTab: StatusTabValue;
    /**
     * True if using keyword search, false if categorySearch
     */
    isKeywordSearch: boolean;
    /**
     *  The keyword being used in the search page
     */
    keyword?: string;
    /**
     * Used to generate the href for the tabs
     */
    pageId?: number;
    /**
     * Used to generate the href for the tabs
     */
    pageName?: string;
};

const statusTabs: { archive: StatusTabData; online: StatusTabData } = {
    archive: {
        label: (
            <span data-walkthrough-step={FirstTimeWalkthroughSteps.Search}>
                <FormattedMessage id="search_facets.price_results_status_label" />
            </span>
        ),
        value: 'archive',
    },
    online: {
        label: <FormattedMessage id="search_facets.upcoming_status_label" />,
        value: 'online',
    },
};

const SearchStatusTabs = ({
    activeTab,
    isKeywordSearch,
    keyword,
    pageId,
    pageName,
}: SearchStatusTabsProps): ReactElement => {
    const navigate = useNavigate();
    const searchPageResultsUrl = (status: string): string => {
        return getSearchUrlWithParams({
            keyword,
            sort: SearchSortAndDirection.MostRelevant,
            status,
        });
    };
    const categoryPriceResultsUrl = getPriceGuideUrl(pageName, pageId);
    const categoryUpcomingUrl = getCategoryLandingPageUrl(pageName, pageId);
    const dispatch = useAppDispatch();
    const currentStep = useAppSelector(onboardingWalkthroughCurrentStep);
    const isOpen = useAppSelector(onboardingWalkthroughIsOpen);

    const getTabHref = (isKeywordSearch: boolean, isToPriceResults: boolean) => {
        if (isKeywordSearch) {
            return isToPriceResults ? searchPageResultsUrl('archive') : searchPageResultsUrl('online');
        }
        return isToPriceResults ? categoryPriceResultsUrl : categoryUpcomingUrl;
    };
    const onChange = (isToPriceResults: boolean) => {
        if ((activeTab === 'archive' && isToPriceResults) || (activeTab === 'online' && !isToPriceResults)) {
            return;
        }
        navigate(getTabHref(isKeywordSearch, isToPriceResults), {
            state: {
                filterAction: isToPriceResults ? FilterAction.ViewPriceResults : FilterAction.ViewUpcomingItems,
            },
        });
    };

    return (
        <Container>
            <Tab
                children={null}
                href={getTabHref(isKeywordSearch, false)}
                label={statusTabs.online.label}
                name={statusTabs.online.value}
                onChange={() => {
                    onChange(false);
                }}
                selectedTabName={activeTab}
            />

            <Tab
                children={null}
                href={getTabHref(isKeywordSearch, true)}
                label={statusTabs.archive.label}
                name={statusTabs.archive.value}
                onChange={() => {
                    onChange(true);
                    // If walkthrough is at the right stage.
                    // If walkthrough is not open, and they click, do they really need the tutorial?
                    if (currentStep === FirstTimeWalkthroughSteps.Search && isOpen) {
                        dispatch(setOnboardingWalkthroughOpenProperty(false));
                        window.setTimeout(() => {
                            firstTimeWalkthroughSkip(dispatch)();
                        }, 400);
                    }
                }}
                selectedTabName={activeTab}
            />
        </Container>
    );
};

export default SearchStatusTabs;

const Container = styled.ul`
    padding: 0;
    display: flex;
    border-bottom: 1px solid ${({ theme }) => theme.colors.grey400};

    & > li {
        list-style-type: none;
    }

    & > * {
        padding-bottom: 6px;

        @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
            padding-bottom: 8px;
        }
    }
`;
